import { Parser, Validator, type AssetId } from "@repo/caip";
import {
  PRICE_IMPACT_BPS_MAX,
  PRICE_IMPACT_BPS_MIN,
  SLIPPAGE_BPS_MAX,
  SLIPPAGE_BPS_MIN,
} from "@repo/constants";
import { ACTION_FAMILY } from "@repo/onchain-actions";
import { z } from "zod";
import { RebalanceBatchArgsSchema } from "@repo/evm-onchain-actions/rebalance-batch-contract";
import { ExternalWithdrawalBatchArgsSchema } from "./external-withdrawal";
export { ExternalWithdrawalBatchArgsSchema } from "./external-withdrawal";
export type { ExternalWithdrawalBatchArgs } from "./external-withdrawal";
import { OwnerWithdrawalBatchArgsSchema } from "./owner-withdrawal";
export { OwnerWithdrawalBatchArgsSchema } from "./owner-withdrawal";
export type { OwnerWithdrawalBatchArgs } from "./owner-withdrawal";

const AmountSchema = z.string().regex(/^\d+$/);
const PositiveAmountSchema = AmountSchema.refine(
  (value) => {
    // The regex check above may have already failed without aborting the
    // parse, so guard against BigInt throwing on non-integer input.
    try {
      return BigInt(value) > 0n;
    } catch {
      return false;
    }
  },
  {
    message: "amount must be a positive integer string",
  },
);
const AssetIdSchema = z.custom<AssetId>(Validator.isValidAssetId, {
  message: "Invalid assetId",
});
const AddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/);
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const SolanaAddressSchema = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/u);
const HexSchema = z.string().regex(/^0x[a-fA-F0-9]+$/);
const PresentObjectSchema = z.object({}).passthrough();

const parseChainRef = (chainRef: string) => {
  try {
    return Parser.parseChainId(chainRef);
  } catch {
    return null;
  }
};

const readEvmChainId = (
  parsed: NonNullable<ReturnType<typeof parseChainRef>>,
): number | null => {
  if (
    parsed.chain.namespace !== "eip155" ||
    !/^\d+$/.test(parsed.chain.reference)
  ) {
    return null;
  }

  const chainId = Number(parsed.chain.reference);
  return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
};

const EvmFundingAccountRefSchema = z.string().refine(
  (accountRef) => {
    try {
      const parsed = Parser.parseAccountId(accountRef);
      return (
        readEvmChainId(parsed) !== null &&
        AddressSchema.safeParse(parsed.account.address).success
      );
    } catch {
      return false;
    }
  },
  { message: "funding account must be a valid EVM account reference" },
);

const canonicalizeParsedChainRef = (
  parsed: NonNullable<ReturnType<typeof parseChainRef>>,
): string => {
  if (parsed.chain.namespace === "eip155") {
    const chainId = readEvmChainId(parsed);
    if (chainId === null) {
      throw new Error("Invalid eip155 chain reference");
    }
    return `eip155:${chainId}`;
  }

  // Non-EVM references can contain case-sensitive base58 identity. Rebuild
  // from parsed components without changing their bytes.
  return `${parsed.chain.namespace}:${parsed.chain.reference}`;
};

export const ExecutionActionScopeSchema = z
  .object({
    portfolioId: z.string().min(1),
    chainRef: z.string().min(1).optional(),
    chainId: z.number().int().positive().optional(),
  })
  .superRefine((scope, ctx) => {
    const hasChainRef = scope.chainRef !== undefined;
    const hasChainId = scope.chainId !== undefined;

    if (hasChainRef === hasChainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "exactly one of chainRef or chainId is required",
        path: [hasChainRef ? "chainRef" : "chainId"],
      });
      return;
    }

    if (scope.chainRef === undefined) {
      return;
    }

    const parsed = parseChainRef(scope.chainRef);
    if (!parsed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "chainRef must be a valid CAIP-2 chain reference",
        path: ["chainRef"],
      });
      return;
    }

    if (parsed.chain.namespace === "eip155") {
      if (readEvmChainId(parsed) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "eip155 chainRef must contain a safe positive decimal chain id",
          path: ["chainRef"],
        });
      }
    }
  })
  .transform((scope) => {
    if (scope.chainRef === undefined && scope.chainId === undefined) {
      throw new Error(
        "ExecutionActionScopeSchema refinement invariant violated",
      );
    }

    const inputChainRef = scope.chainRef ?? `eip155:${scope.chainId}`;
    const parsed = Parser.parseChainId(inputChainRef);
    const chainRef = canonicalizeParsedChainRef(parsed);
    const chainId =
      parsed.chain.namespace === "eip155"
        ? (readEvmChainId(parsed) ?? undefined)
        : undefined;

    return {
      portfolioId: scope.portfolioId,
      chainRef,
      ...(chainId === undefined ? {} : { chainId }),
    };
  });

const NormalizedExecutionActionScopeSchema = z
  .object({
    portfolioId: z.string().min(1),
    chainRef: z.string().min(1),
    chainId: z.number().int().positive().optional(),
  })
  .superRefine((scope, ctx) => {
    const parsed = parseChainRef(scope.chainRef);
    if (!parsed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "chainRef must be a valid CAIP-2 chain reference",
        path: ["chainRef"],
      });
      return;
    }

    if (parsed.chain.namespace === "eip155") {
      const derivedChainId = readEvmChainId(parsed);
      if (derivedChainId === null || scope.chainId !== derivedChainId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "chainId must match the eip155 chainRef",
          path: ["chainId"],
        });
      }
      return;
    }

    if (scope.chainId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "chainId is only valid for an eip155 chainRef",
        path: ["chainId"],
      });
    }
  })
  .transform((scope) => {
    const parsed = Parser.parseChainId(scope.chainRef);
    return {
      ...scope,
      chainRef: canonicalizeParsedChainRef(parsed),
    };
  });

/**
 * Cross-service wire form. Raw callers still go through
 * ExecutionActionScopeSchema's exactly-one-of check; this schema additionally
 * accepts the matching chainRef + derived EVM chainId emitted by that parser,
 * so a validated scope remains valid after JSON serialization and reparse.
 */
export const ExecutionActionScopeWireSchema = z.union([
  ExecutionActionScopeSchema,
  NormalizedExecutionActionScopeSchema,
]);

export const ExecutionActionFailurePolicySchema = z.enum([
  "fail_fast",
  "continue",
]);

const ExternalTransferArgsSchema = z.object({
  assetId: AssetIdSchema,
  amount: AmountSchema,
  to: z.string().min(1),
});

const PortfolioTransferArgsSchema = z.object({
  assetId: AssetIdSchema,
  amount: AmountSchema,
  toPortfolioId: z.string().trim().min(1),
});

export const ExecutionTransferArgsSchema = z.union([
  ExternalTransferArgsSchema,
  PortfolioTransferArgsSchema,
]);

const slippageBpsSchema = z
  .number()
  .int()
  .min(SLIPPAGE_BPS_MIN)
  .max(SLIPPAGE_BPS_MAX)
  .optional();
const maxPriceImpactBpsSchema = z
  .number()
  .int()
  .min(PRICE_IMPACT_BPS_MIN)
  .max(PRICE_IMPACT_BPS_MAX)
  .optional();
const feePercentSchema = z
  .number()
  .finite()
  .min(0)
  .max(1)
  .nullable()
  .optional();
const executionProviderSchema = z
  .enum(["lifi", "relay", "bebop_rfq", "bebop_jam", "ondo_direct"])
  .optional();
const bebopRfqExpiryTypeSchema = z.enum(["standard", "short"]).optional();
const bebopRfqFeeBpsSchema = z.number().int().min(0).max(500).optional();
const PreTradeAccountingGuardWatermarkValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

export const ExecutionPreTradeAccountingGuardSchema = z.object({
  kind: z.literal("indexer_v4_accounting_pnl"),
  guardSource: z.literal("matcher_trigger"),
  fromAssetId: AssetIdSchema,
  assetId: z.string().trim().min(1),
  exposureId: z.string().trim().min(1),
  signalSnapshotId: z.string().trim().min(1),
  accountingStreamVersion: z.string().trim().min(1),
  sourceFinality: z.enum([
    "observed",
    "sequenced",
    "safe",
    "finalized",
    "orphaned",
  ]),
  status: z.enum([
    "ready_finalized",
    "ready_provisional",
    "partial",
    "unavailable",
  ]),
  actionable: z.boolean(),
  basisCompletenessRatio: z.string().trim().min(1),
  valuationCompletenessRatio: z.string().trim().min(1),
  unknownBasisQuantityBaseUnits: AmountSchema,
  metric: z.enum(["cost_basis_multiple", "unrealized_pnl_ratio"]),
  threshold: z.string().trim().min(1),
  observedValue: z.string().trim().min(1),
  allowProvisional: z.boolean().optional(),
  sourceEventCount: z.number().int().nonnegative(),
  sourceEventWatermark: z.record(
    z.string(),
    PreTradeAccountingGuardWatermarkValueSchema,
  ),
});

const ExecutionSwapBaseArgsSchema = z.object({
  appFee: z.never().optional(),
  executionProvider: executionProviderSchema,
  expiryType: bebopRfqExpiryTypeSchema,
  feeBps: bebopRfqFeeBpsSchema,
  fromAssetId: AssetIdSchema,
  toAssetId: AssetIdSchema,
  amount: AmountSchema,
  slippageBps: slippageBpsSchema,
  maxPriceImpactBps: maxPriceImpactBpsSchema,
  feePercent: feePercentSchema,
  preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
});

const ExecutionSwapExternalDestinationArgsSchema =
  ExecutionSwapBaseArgsSchema.extend({
    to: AddressSchema,
  });

const ExecutionSwapPortfolioDestinationArgsSchema =
  ExecutionSwapBaseArgsSchema.extend({
    toPortfolioId: z.string().trim().min(1),
  });

const ExecutionOndoDirectBatchLegAssetSchema = z.object({
  assetId: AssetIdSchema,
  assetTokenAddress: AddressSchema.optional(),
});
const ExecutionOndoDirectBatchSellLegSchema =
  ExecutionOndoDirectBatchLegAssetSchema.extend({
    amount: PositiveAmountSchema,
    // The planner deliberately allows a 0 minimum.
    minCashOutRaw: AmountSchema.optional(),
  });
const ExecutionOndoDirectBatchBuyLegSchema =
  ExecutionOndoDirectBatchLegAssetSchema.extend({
    cashAmount: PositiveAmountSchema,
    // The planner deliberately allows a 0 minimum.
    minTokenOutRaw: AmountSchema.optional(),
  });
const ExecutionOndoDirectBatchBaseArgsSchema = z.object({
  quoteMode: z.literal("fetch"),
  marketHoursPolicy: z.enum(["hold", "reject"]).optional(),
  cashAssetId: AssetIdSchema,
  cashTokenAddress: AddressSchema,
  cashInputAmount: PositiveAmountSchema.optional(),
  sells: z.array(ExecutionOndoDirectBatchSellLegSchema).max(50).optional(),
  buys: z.array(ExecutionOndoDirectBatchBuyLegSchema).max(50).optional(),
  residualCashPolicy: z.literal("sweep_to_user").optional(),
  approvalMode: z
    .enum(["require_existing", "submit_missing_approvals"])
    .optional(),
});

type ExecutionOndoDirectBatchBaseArgs = z.infer<
  typeof ExecutionOndoDirectBatchBaseArgsSchema
>;

const EIP155_ASSET_CHAIN_PATTERN = /^eip155:(\d+)[:/]/;

const parseEip155AssetChainId = (assetId: string): number | null => {
  const match = EIP155_ASSET_CHAIN_PATTERN.exec(assetId);
  if (!match?.[1]) {
    return null;
  }
  const chainId = Number(match[1]);
  return Number.isSafeInteger(chainId) ? chainId : null;
};

const isEip155Erc20AssetOnChain = (
  assetId: string,
  chainId: number,
): boolean => {
  try {
    const parsed = Parser.parseAssetId(assetId);
    return (
      parsed.chain.namespace === "eip155" &&
      parsed.chain.reference === String(chainId) &&
      parsed.asset.namespace === "erc20"
    );
  } catch {
    return false;
  }
};

const listOndoDirectBatchLegs = (args: ExecutionOndoDirectBatchBaseArgs) => [
  ...(args.sells ?? []).map((leg, index) => ({
    leg,
    side: "sells" as const,
    index,
  })),
  ...(args.buys ?? []).map((leg, index) => ({
    leg,
    side: "buys" as const,
    index,
  })),
];

const refineOndoDirectBatchArgs = (
  args: ExecutionOndoDirectBatchBaseArgs,
  ctx: z.RefinementCtx,
): void => {
  const legs = listOndoDirectBatchLegs(args);
  if (legs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "at least one Ondo Direct batch leg is required",
    });
    return;
  }

  const cashAssetKey = args.cashAssetId.toLowerCase();
  const cashTokenKey = args.cashTokenAddress.toLowerCase();
  const cashChainId = parseEip155AssetChainId(args.cashAssetId);
  const seenAssetIds = new Set<string>();
  const seenTokenAddresses = new Set<string>();

  for (const { leg, side, index } of legs) {
    const assetKey = leg.assetId.toLowerCase();
    if (assetKey === cashAssetKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ondo Direct batch leg assetId must differ from cashAssetId",
        path: [side, index, "assetId"],
      });
    }
    if (seenAssetIds.has(assetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "duplicate assetId across Ondo Direct batch legs",
        path: [side, index, "assetId"],
      });
    }
    seenAssetIds.add(assetKey);

    const legChainId = parseEip155AssetChainId(leg.assetId);
    if (
      cashChainId !== null &&
      legChainId !== null &&
      legChainId !== cashChainId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Ondo Direct batch leg assetId must reference the same chain as cashAssetId",
        path: [side, index, "assetId"],
      });
    }

    if (leg.assetTokenAddress !== undefined) {
      const tokenKey = leg.assetTokenAddress.toLowerCase();
      if (tokenKey === cashTokenKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Ondo Direct batch leg assetTokenAddress must differ from cashTokenAddress",
          path: [side, index, "assetTokenAddress"],
        });
      }
      if (seenTokenAddresses.has(tokenKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "duplicate assetTokenAddress across Ondo Direct batch legs",
          path: [side, index, "assetTokenAddress"],
        });
      }
      seenTokenAddresses.add(tokenKey);
    }
  }
};

const ExecutionOndoDirectBatchArgsSchema =
  ExecutionOndoDirectBatchBaseArgsSchema.extend({
    executionProvider: z.literal("ondo_direct"),
  }).superRefine(refineOndoDirectBatchArgs);

const ExecutionOndoDirectBatchRebalanceArgsSchema =
  ExecutionOndoDirectBatchBaseArgsSchema.superRefine(refineOndoDirectBatchArgs);

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasDefinedKey = (value: Record<string, unknown>, key: string): boolean =>
  key in value && value[key] !== undefined;

/**
 * True when a swap payload presents Ondo Direct batch legs (`sells`/`buys`
 * keys), regardless of the declared execution provider. Such payloads must
 * parse as a valid batch — falling through to the legacy single-swap schemas
 * (which strip unknown keys) would silently downgrade a batch intent into a
 * single swap.
 */
const presentsOndoDirectBatchLegs = (value: unknown): boolean =>
  isPlainRecord(value) &&
  (hasDefinedKey(value, "sells") || hasDefinedKey(value, "buys"));

export const ExecutionSwapArgsSchema = z
  .unknown()
  .superRefine((value, ctx) => {
    if (isPlainRecord(value) && hasDefinedKey(value, "appFee")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "app fees require an explicit supported Relay action",
      });
    }
    if (
      isPlainRecord(value) &&
      value.executionProvider === "relay" &&
      ((hasDefinedKey(value, "quoteMode") && value.quoteMode !== "fetch") ||
        hasDefinedKey(value, "route"))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Relay swap args only support fetched quotes",
      });
    }
    if (!presentsOndoDirectBatchLegs(value)) {
      return;
    }
    const batchResult = ExecutionOndoDirectBatchArgsSchema.safeParse(value);
    if (batchResult.success) {
      return;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "swap args present batch legs (sells/buys) but do not match the Ondo Direct batch schema; refusing to downgrade to a single swap",
    });
    for (const issue of batchResult.error.issues) {
      ctx.addIssue({ ...issue });
    }
  })
  .pipe(
    z.union([
      z.object({
        quoteMode: z.literal("provided"),
        executionProvider: z.literal("lifi").optional(),
        route: PresentObjectSchema,
        slippageBps: slippageBpsSchema,
      }),
      ExecutionOndoDirectBatchArgsSchema,
      ExecutionSwapExternalDestinationArgsSchema,
      ExecutionSwapPortfolioDestinationArgsSchema,
      ExecutionSwapBaseArgsSchema,
    ]),
  );

const ExecutionLifiSwapProvidedArgsSchema = z.object({
  quoteMode: z.literal("provided"),
  route: PresentObjectSchema,
  slippageBps: slippageBpsSchema,
  nonceScopeId: z.string().min(1).optional(),
  strategyInstanceId: z.string().min(1).optional(),
  stepIndex: z.number().int().nonnegative().optional(),
});

const ExecutionLifiSwapFetchArgsSchema = z.object({
  quoteMode: z.literal("fetch"),
  fromTokenAddress: AddressSchema,
  toTokenAddress: AddressSchema,
  amount: AmountSchema,
  slippageBps: slippageBpsSchema,
  maxPriceImpactBps: maxPriceImpactBpsSchema,
  feePercent: feePercentSchema,
  routeOptions: z
    .object({
      exchanges: z
        .object({
          allow: z.array(z.string().trim().min(1)).min(1).optional(),
          deny: z.array(z.string().trim().min(1)).min(1).optional(),
          prefer: z.array(z.string().trim().min(1)).min(1).optional(),
        })
        .strict()
        .optional(),
    })
    .strict()
    .optional(),
  to: AddressSchema.optional(),
  toPortfolioId: z.string().trim().min(1).optional(),
  nonceScopeId: z.string().min(1).optional(),
  strategyInstanceId: z.string().min(1).optional(),
  stepIndex: z.number().int().nonnegative().optional(),
  preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
});

export const ExecutionLifiSwapArgsSchema = z.union([
  ExecutionLifiSwapProvidedArgsSchema,
  ExecutionLifiSwapFetchArgsSchema,
]);

export const ExecutionRelayAppFeePolicySchema = z
  .object({
    recipient: AddressSchema.refine(
      (value) => value.toLowerCase() !== ZERO_ADDRESS,
      {
        message: "app fee recipient must be nonzero",
      },
    ).transform((value) => value.toLowerCase()),
    feeBps: z.number().int().min(1).max(500),
  })
  .strict();

export const ExecutionRelaySwapArgsSchema = z.object({
  quoteMode: z.literal("fetch").default("fetch"),
  route: z.never().optional(),
  tradeType: z.never().optional(),
  fromTokenAddress: AddressSchema,
  toTokenAddress: AddressSchema,
  amount: AmountSchema,
  slippageBps: slippageBpsSchema,
  maxPriceImpactBps: maxPriceImpactBpsSchema,
  appFee: ExecutionRelayAppFeePolicySchema.optional(),
  appFees: z.never().optional(),
  feeBps: z.never().optional(),
  feePercent: z.never().optional(),
  to: AddressSchema.optional(),
  toPortfolioId: z.string().trim().min(1).optional(),
  preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
  fundingSourceAccountRef: EvmFundingAccountRefSchema.optional(),
});

export const ExecutionRelayBridgeArgsSchema = z.object({
  quoteMode: z.never().optional(),
  route: z.never().optional(),
  assetId: AssetIdSchema,
  amount: AmountSchema,
  destinationChainId: z.number().int().positive(),
  destinationAssetId: AssetIdSchema,
  slippageBps: slippageBpsSchema,
  appFee: ExecutionRelayAppFeePolicySchema.optional(),
  appFees: z.never().optional(),
  feeBps: z.never().optional(),
  feePercent: z.never().optional(),
  expectedAmountMinRaw: z
    .string()
    .regex(/^[1-9]\d*$/)
    .optional(),
  bridgeIntentPurpose: z.literal("fund_leg").optional(),
  fundingSourceAccountRef: EvmFundingAccountRefSchema.optional(),
  fundingDestinationAccountRef: EvmFundingAccountRefSchema.optional(),
});

export const ExecutionBridgeArgsSchema = z.object({
  appFee: z.never().optional(),
  fromAssetId: AssetIdSchema,
  toAssetId: AssetIdSchema,
  amount: AmountSchema,
  slippageBps: slippageBpsSchema,
});

const ExecutionYieldActionArgsSchema = z.object({
  assetId: AssetIdSchema,
  amount: AmountSchema,
  protocolId: z.string().min(1),
});

export const ExecutionYieldWithdrawArgsSchema = ExecutionYieldActionArgsSchema;

export const ExecutionYieldDepositArgsSchema = ExecutionYieldActionArgsSchema;

export const ExecutionYieldRedeemArgsSchema = z.object({
  shareAssetId: AssetIdSchema,
  amount: AmountSchema,
  protocolId: z.string().min(1),
  useMaxBalance: z.boolean().optional(),
});

export const ExecutionCallArgsSchema = z.object({
  to: AddressSchema,
  data: HexSchema,
  value: z.string().min(1).optional(),
});

const createCategoryActionInputSchema = <
  TType extends string,
  TArgsSchema extends z.ZodTypeAny,
>(params: {
  type: TType;
  args: TArgsSchema;
}) =>
  z.object({
    type: z.literal(params.type),
    scope: ExecutionActionScopeSchema,
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
    args: params.args,
  });

const createExplicitActionInputSchema = <
  TType extends string,
  TArgsSchema extends z.ZodTypeAny,
>(params: {
  type: TType;
  args: TArgsSchema;
}) =>
  z.object({
    type: z.literal(params.type),
    scope: ExecutionActionScopeSchema,
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
    args: params.args,
  });

const TransferActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.transfer,
  args: ExecutionTransferArgsSchema,
});
const YieldWithdrawActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.yieldWithdraw,
  args: ExecutionYieldWithdrawArgsSchema,
});
const YieldRedeemActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.yieldRedeem,
  args: ExecutionYieldRedeemArgsSchema,
});
const SwapActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.swap,
  args: ExecutionSwapArgsSchema,
});
const BridgeActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.bridge,
  args: ExecutionBridgeArgsSchema,
});
const YieldDepositActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.yieldDeposit,
  args: ExecutionYieldDepositArgsSchema,
});
const CallActionInputSchema = createCategoryActionInputSchema({
  type: ACTION_FAMILY.call,
  args: ExecutionCallArgsSchema,
});

const Erc20TransferActionInputSchema = createExplicitActionInputSchema({
  type: "erc20_transfer",
  args: z.object({
    tokenAddress: AddressSchema,
    recipient: AddressSchema,
    amount: AmountSchema,
  }),
});
const OwnerWithdrawalBatchActionInputSchema = createExplicitActionInputSchema({
  type: "owner_withdrawal_batch",
  args: OwnerWithdrawalBatchArgsSchema,
});
const ExternalWithdrawalBatchActionInputSchema =
  createExplicitActionInputSchema({
    type: "external_withdrawal_batch",
    args: ExternalWithdrawalBatchArgsSchema,
  });
const FloatTransferActionInputSchema = createExplicitActionInputSchema({
  type: "float_transfer",
  args: z.object({
    attemptIndex: z.number().int().nonnegative(),
    assetId: AssetIdSchema,
    amount: PositiveAmountSchema,
    expectedAmountMinRaw: PositiveAmountSchema,
    fundingSourceAccountRef: z.string().trim().min(1),
    fundingDestinationAccountRef: z.string().trim().min(1),
  }),
});
const SplTransferActionInputSchema = createExplicitActionInputSchema({
  type: "spl_transfer",
  args: z.object({
    assetId: AssetIdSchema,
    amount: AmountSchema,
    to: SolanaAddressSchema,
  }),
});
const DeliverProceedsActionInputSchema = createExplicitActionInputSchema({
  type: "deliver_proceeds",
  args: z.object({
    recipient: AddressSchema,
    tokenAddress: AddressSchema,
    sources: z
      .array(
        z.object({
          actionIndex: z.number().int().nonnegative(),
        }),
      )
      .min(1),
    partialDelivery: z.enum(["deliver", "withhold"]),
  }),
});
const NativeTransferActionInputSchema = createExplicitActionInputSchema({
  type: "native_transfer",
  args: z.object({
    recipient: AddressSchema,
    amount: AmountSchema,
  }),
});
const BebopRfqSwapActionInputSchema = createExplicitActionInputSchema({
  type: "bebop_rfq_swap",
  args: z.object({
    quoteMode: z.literal("fetch"),
    fromTokenAddress: AddressSchema,
    toTokenAddress: AddressSchema,
    amount: AmountSchema,
    expiryType: bebopRfqExpiryTypeSchema,
    feeBps: bebopRfqFeeBpsSchema,
    to: AddressSchema.optional(),
    toPortfolioId: z.string().trim().min(1).optional(),
  }),
});
const BebopJamSwapActionInputSchema = createExplicitActionInputSchema({
  type: "bebop_jam_swap",
  args: z.object({
    quoteMode: z.literal("fetch"),
    fromTokenAddress: AddressSchema,
    toTokenAddress: AddressSchema,
    amount: AmountSchema,
    slippageBps: slippageBpsSchema,
    feeBps: bebopRfqFeeBpsSchema,
    to: AddressSchema.optional(),
    toPortfolioId: z.string().trim().min(1).optional(),
  }),
});
const OndoDirectMintRedeemActionInputSchema = createExplicitActionInputSchema({
  type: "ondo_direct_mint_redeem",
  args: z.object({
    quoteMode: z.literal("fetch"),
    side: z.enum(["buy", "sell"]),
    sellAssetId: AssetIdSchema,
    buyAssetId: AssetIdSchema,
    sellAmount: AmountSchema,
    cashTokenAddress: AddressSchema,
    assetTokenAddress: AddressSchema,
    marketHoursPolicy: z.enum(["hold", "reject"]).optional(),
    to: AddressSchema.optional(),
    toPortfolioId: z.string().trim().min(1).optional(),
  }),
});
const OndoDirectBatchRebalanceActionInputSchema =
  createExplicitActionInputSchema({
    type: "ondo_direct_batch_rebalance",
    args: ExecutionOndoDirectBatchRebalanceArgsSchema,
  });
const RealityViaBitgetSwapActionInputSchema = createExplicitActionInputSchema({
  type: "reality_via_bitget_swap",
  args: z
    .object({
      quoteMode: z.literal("fetch"),
      sellAssetId: AssetIdSchema,
      buyAssetId: AssetIdSchema,
      sellAmount: PositiveAmountSchema,
      realityAssetId: AssetIdSchema,
      realitySide: z.enum(["buy", "sell"]),
      integrationMode: z.literal("instruction"),
      recipient: z.literal("vault"),
      feePolicyRef: z.literal("bitget-default-v1"),
      slippageBps: z.literal(100),
    })
    .superRefine((args, ctx) => {
      const expectedRealityAssetId =
        args.realitySide === "buy" ? args.buyAssetId : args.sellAssetId;
      if (args.realityAssetId !== expectedRealityAssetId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "realityAssetId must identify the Reality leg selected by realitySide",
          path: ["realityAssetId"],
        });
      }
    }),
});
const LifiSwapActionInputSchema = z.object({
  type: z.literal("lifi_swap"),
  scope: ExecutionActionScopeSchema,
  failurePolicy: ExecutionActionFailurePolicySchema.optional(),
  args: ExecutionLifiSwapArgsSchema,
});
const RelaySwapActionInputSchema = createExplicitActionInputSchema({
  type: "relay_swap",
  args: ExecutionRelaySwapArgsSchema,
});
const RelayBridgeActionInputSchema = createExplicitActionInputSchema({
  type: "relay_bridge",
  args: ExecutionRelayBridgeArgsSchema,
});
const LifiBridgeActionInputSchema = createExplicitActionInputSchema({
  type: "lifi_bridge",
  args: z.union([
    z.discriminatedUnion("quoteMode", [
      z.object({
        quoteMode: z.literal("provided"),
        fromChainId: z.number().int().positive(),
        toChainId: z.number().int().positive(),
        route: PresentObjectSchema,
        slippageBps: slippageBpsSchema,
        nonceScopeId: z.string().min(1).optional(),
        strategyInstanceId: z.string().min(1).optional(),
        stepIndex: z.number().int().nonnegative().optional(),
      }),
      z.object({
        quoteMode: z.literal("fetch"),
        fromChainId: z.number().int().positive(),
        toChainId: z.number().int().positive(),
        fromTokenAddress: AddressSchema,
        toTokenAddress: AddressSchema,
        amount: AmountSchema,
        toAddress: AddressSchema.optional(),
        slippageBps: slippageBpsSchema,
        nonceScopeId: z.string().min(1).optional(),
        strategyInstanceId: z.string().min(1).optional(),
        stepIndex: z.number().int().nonnegative().optional(),
      }),
    ]),
    z.object({
      assetId: AssetIdSchema,
      amount: AmountSchema,
      destinationChainId: z.number().int().positive(),
      destinationAssetId: AssetIdSchema,
      slippageBps: slippageBpsSchema,
    }),
  ]),
});
const Erc4626DepositActionInputSchema = createExplicitActionInputSchema({
  type: "erc4626_deposit",
  args: z.object({
    tokenAddress: AddressSchema,
    vaultAddress: AddressSchema,
    amount: PositiveAmountSchema,
  }),
});
const Erc4626WithdrawActionInputSchema = createExplicitActionInputSchema({
  type: "erc4626_withdraw",
  args: z.object({
    vaultAddress: AddressSchema,
    amount: PositiveAmountSchema,
    receiver: AddressSchema.optional(),
    owner: AddressSchema.optional(),
  }),
});
const Erc4626RedeemActionInputSchema = createExplicitActionInputSchema({
  type: "erc4626_redeem",
  args: z.object({
    vaultAddress: AddressSchema,
    amount: PositiveAmountSchema,
    receiver: AddressSchema.optional(),
    owner: AddressSchema.optional(),
    useMaxBalance: z.boolean().optional(),
    expectedFullBalanceBase: PositiveAmountSchema.optional(),
  }),
});
const ApprovedVaultActionArgsSchema = z.object({
  vaultId: z.string().min(1),
  amount: AmountSchema,
});
const FluidVaultDepositActionInputSchema = createExplicitActionInputSchema({
  type: "fluid_vault_deposit",
  args: ApprovedVaultActionArgsSchema,
});
const FluidVaultWithdrawActionInputSchema = createExplicitActionInputSchema({
  type: "fluid_vault_withdraw",
  args: ApprovedVaultActionArgsSchema,
});
const FluidVaultRedeemActionInputSchema = createExplicitActionInputSchema({
  type: "fluid_vault_redeem",
  args: ApprovedVaultActionArgsSchema,
});
const MorphoVaultDepositActionInputSchema = createExplicitActionInputSchema({
  type: "morpho_vault_deposit",
  args: ApprovedVaultActionArgsSchema,
});
const MorphoVaultWithdrawActionInputSchema = createExplicitActionInputSchema({
  type: "morpho_vault_withdraw",
  args: ApprovedVaultActionArgsSchema,
});
const MorphoVaultRedeemActionInputSchema = createExplicitActionInputSchema({
  type: "morpho_vault_redeem",
  args: ApprovedVaultActionArgsSchema,
});
const AaveV3DepositActionInputSchema = createExplicitActionInputSchema({
  type: "aave_v3_deposit",
  args: z.object({
    tokenAddress: AddressSchema,
    poolAddress: AddressSchema,
    amount: AmountSchema,
    onBehalfOf: AddressSchema.optional(),
  }),
});
const AaveV3WithdrawActionInputSchema = createExplicitActionInputSchema({
  type: "aave_v3_withdraw",
  args: z.object({
    tokenAddress: AddressSchema,
    poolAddress: AddressSchema,
    amount: AmountSchema,
    recipient: AddressSchema.optional(),
  }),
});
const AaveV3GatewayDepositActionInputSchema = createExplicitActionInputSchema({
  type: "aave_v3_gateway_deposit",
  args: z.object({
    gatewayAddress: AddressSchema,
    lendingPool: AddressSchema,
    amount: AmountSchema,
    onBehalfOf: AddressSchema.optional(),
    referralCode: z.number().int().min(0).max(65_535).optional(),
  }),
});
const AaveV3GatewayWithdrawActionInputSchema = createExplicitActionInputSchema({
  type: "aave_v3_gateway_withdraw",
  args: z.object({
    gatewayAddress: AddressSchema,
    lendingPool: AddressSchema,
    amount: AmountSchema,
    recipient: AddressSchema.optional(),
  }),
});
const WethWrapActionInputSchema = createExplicitActionInputSchema({
  type: "weth_wrap",
  args: z.object({
    wethAddress: AddressSchema,
    amount: AmountSchema,
  }),
});
const WethUnwrapActionInputSchema = createExplicitActionInputSchema({
  type: "weth_unwrap",
  args: z.object({
    wethAddress: AddressSchema,
    amount: AmountSchema,
  }),
});
const ZeroExSwapActionInputSchema = createExplicitActionInputSchema({
  type: "zeroex_swap",
  args: z.object({
    quote: z
      .object({
        sellToken: AddressSchema.optional(),
        buyToken: AddressSchema.optional(),
        sellAmount: AmountSchema.optional(),
        transaction: z.object({
          to: AddressSchema,
          data: HexSchema,
          value: z.union([z.string(), z.number(), z.bigint()]).optional(),
        }),
        issues: z
          .object({
            allowance: z
              .object({
                spender: AddressSchema.optional(),
              })
              .optional(),
          })
          .optional(),
        meta: z
          .object({
            swapId: z.string().min(1).optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    nonceScopeId: z.string().min(1).optional(),
    strategyInstanceId: z.string().min(1).optional(),
  }),
});
const Erc20ApproveActionInputSchema = createExplicitActionInputSchema({
  type: "erc20_approve",
  args: z.object({
    tokenAddress: AddressSchema,
    spender: AddressSchema,
    amount: AmountSchema,
  }),
});
const EvmCallActionInputSchema = createExplicitActionInputSchema({
  type: "evm_call",
  args: ExecutionCallArgsSchema,
});

/**
 * Generic rebalance batch action. One action == one atomic multi-call EVM
 * userOp carrying the ordered calls produced by the rebalance translator's
 * generic-evm adapter. Each call is a typed leg (approve/swap/deposit/...)
 * sized in raw token units.
 *
 * See features/rebalance-engine-v2/TECHNICAL_DESIGN.md.
 */
const RebalanceBatchActionInputSchema = createExplicitActionInputSchema({
  type: "rebalance_batch",
  args: RebalanceBatchArgsSchema,
});

const addOndoDirectBatchScopeChainIssues = (params: {
  readonly chainId?: number;
  readonly args: ExecutionOndoDirectBatchBaseArgs;
  readonly ctx: z.RefinementCtx;
}): void => {
  const { chainId, args, ctx } = params;
  if (chainId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Ondo Direct batch execution requires an eip155 scope",
      path: ["scope", "chainRef"],
    });
    return;
  }
  const entries = [
    { assetId: args.cashAssetId, path: ["args", "cashAssetId"] },
    ...listOndoDirectBatchLegs(args).map(({ leg, side, index }) => ({
      assetId: leg.assetId,
      path: ["args", side, index, "assetId"],
    })),
  ];
  for (const entry of entries) {
    const assetChainId = parseEip155AssetChainId(entry.assetId);
    if (assetChainId !== null && assetChainId !== chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Ondo Direct batch assetId must reference the same chain as scope.chainId",
        path: entry.path,
      });
    }
  }
};

const addRealityViaBitgetScopeChainIssues = (params: {
  readonly chainId?: number;
  readonly args: {
    readonly sellAssetId: AssetId;
    readonly buyAssetId: AssetId;
  };
  readonly ctx: z.RefinementCtx;
}): void => {
  const { chainId, args, ctx } = params;
  if (chainId !== 42161) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Reality via Bitget execution requires an eip155:42161 scope",
      path: ["scope", "chainRef"],
    });
  }

  const assetEntries: Array<{
    readonly assetId: AssetId;
    readonly path: (string | number)[];
  }> = [
    { assetId: args.sellAssetId, path: ["args", "sellAssetId"] },
    { assetId: args.buyAssetId, path: ["args", "buyAssetId"] },
  ];
  for (const entry of assetEntries) {
    if (!isEip155Erc20AssetOnChain(entry.assetId, 42161)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Reality via Bitget assetId must be an ERC-20 on eip155:42161",
        path: entry.path,
      });
    }
  }
};

// Concentrated-liquidity family (dual-investment accumulator). Ticks arrive concrete
// from the automation layer; MIN/MAX bound per v3 TickMath domain.
const TickSchema = z.number().int().min(-887272).max(887272);
const ClPositionTokenIdSchema = z
  .string()
  .regex(/^\d+$/, "tokenId must be a decimal string");
const ClDeadlineSchema = z.string().regex(/^\d+$/).optional();
// NOTE: the "liquidity" FAMILY alias is intentionally absent from the execute
// union — with five cl_* candidates it can never disambiguate (every request
// would be ACTION_ALIAS_AMBIGUOUS), so explicit cl_* types are the only
// accepted inputs until a verb discriminator exists.
const ClMintActionInputSchema = createExplicitActionInputSchema({
  type: "cl_mint",
  args: z
    .object({
      poolId: z.string().min(1),
      tickLower: TickSchema,
      tickUpper: TickSchema,
      quoteAmountRaw: PositiveAmountSchema,
      deadline: ClDeadlineSchema,
    })
    .refine((a) => a.tickLower < a.tickUpper, {
      message: "tickLower must be below tickUpper",
    }),
});
const ClIncreaseActionInputSchema = createExplicitActionInputSchema({
  type: "cl_increase",
  args: z.object({
    poolId: z.string().min(1),
    tokenId: ClPositionTokenIdSchema,
    quoteAmountRaw: PositiveAmountSchema,
    deadline: ClDeadlineSchema,
  }),
});
const ClDecreaseActionInputSchema = createExplicitActionInputSchema({
  type: "cl_decrease",
  args: z
    .object({
      poolId: z.string().min(1),
      tokenId: ClPositionTokenIdSchema,
      liquidity: PositiveAmountSchema,
      amount0MinRaw: AmountSchema,
      amount1MinRaw: AmountSchema,
      deadline: ClDeadlineSchema,
      burnAfterCollect: z.boolean().optional(),
      transferLegs: z
        .tuple([
          z.object({ tokenAddress: AddressSchema, to: AddressSchema }).strict(),
          z.object({ tokenAddress: AddressSchema, to: AddressSchema }).strict(),
        ])
        .optional(),
    })
    .superRefine((args, ctx) => {
      if (args.transferLegs && args.burnAfterCollect !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "delivery transfer legs require burnAfterCollect",
          path: ["burnAfterCollect"],
        });
      }
      if (
        args.transferLegs &&
        args.transferLegs[0].to.toLowerCase() !==
          args.transferLegs[1].to.toLowerCase()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "delivery transfer legs require one shared recipient",
          path: ["transferLegs", 1, "to"],
        });
      }
      if (
        args.transferLegs?.some((leg) => leg.to.toLowerCase() === ZERO_ADDRESS)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "delivery transfer-leg recipient must not be zero",
          path: ["transferLegs"],
        });
      }
    }),
});
const ClCollectActionInputSchema = createExplicitActionInputSchema({
  type: "cl_collect",
  args: z.object({
    poolId: z.string().min(1),
    tokenId: ClPositionTokenIdSchema,
  }),
});
const ClBurnActionInputSchema = createExplicitActionInputSchema({
  type: "cl_burn",
  args: z.object({
    poolId: z.string().min(1),
    tokenId: ClPositionTokenIdSchema,
  }),
});

const ExecuteActionInputUnionSchema = z.discriminatedUnion("type", [
  TransferActionInputSchema,
  YieldWithdrawActionInputSchema,
  YieldRedeemActionInputSchema,
  SwapActionInputSchema,
  BridgeActionInputSchema,
  YieldDepositActionInputSchema,
  CallActionInputSchema,
  Erc20TransferActionInputSchema,
  OwnerWithdrawalBatchActionInputSchema,
  ExternalWithdrawalBatchActionInputSchema,
  FloatTransferActionInputSchema,
  SplTransferActionInputSchema,
  DeliverProceedsActionInputSchema,
  NativeTransferActionInputSchema,
  BebopRfqSwapActionInputSchema,
  BebopJamSwapActionInputSchema,
  OndoDirectMintRedeemActionInputSchema,
  OndoDirectBatchRebalanceActionInputSchema,
  RealityViaBitgetSwapActionInputSchema,
  LifiSwapActionInputSchema,
  LifiBridgeActionInputSchema,
  RelaySwapActionInputSchema,
  RelayBridgeActionInputSchema,
  Erc4626DepositActionInputSchema,
  Erc4626WithdrawActionInputSchema,
  Erc4626RedeemActionInputSchema,
  FluidVaultDepositActionInputSchema,
  FluidVaultWithdrawActionInputSchema,
  FluidVaultRedeemActionInputSchema,
  MorphoVaultDepositActionInputSchema,
  MorphoVaultWithdrawActionInputSchema,
  MorphoVaultRedeemActionInputSchema,
  AaveV3DepositActionInputSchema,
  AaveV3WithdrawActionInputSchema,
  AaveV3GatewayDepositActionInputSchema,
  AaveV3GatewayWithdrawActionInputSchema,
  WethWrapActionInputSchema,
  WethUnwrapActionInputSchema,
  ZeroExSwapActionInputSchema,
  Erc20ApproveActionInputSchema,
  EvmCallActionInputSchema,
  RebalanceBatchActionInputSchema,
  ClMintActionInputSchema,
  ClIncreaseActionInputSchema,
  ClDecreaseActionInputSchema,
  ClCollectActionInputSchema,
  ClBurnActionInputSchema,
]);

export const ExecuteActionInputSchema = z
  .unknown()
  .superRefine((input, ctx) => {
    if (
      isPlainRecord(input) &&
      input.type !== "relay_swap" &&
      input.type !== "relay_bridge" &&
      isPlainRecord(input.args) &&
      hasDefinedKey(input.args, "appFee")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "app fees require an explicit supported Relay action",
        path: ["args", "appFee"],
      });
    }
  })
  .pipe(ExecuteActionInputUnionSchema)
  .superRefine((action, ctx) => {
    if (
      action.type === "owner_withdrawal_batch" ||
      action.type === "external_withdrawal_batch"
    ) {
      if (
        !action.args.executionAccountRef.startsWith(`${action.scope.chainRef}:`)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["args", "executionAccountRef"],
          message: "Execution account must match the action chain",
        });
      }
      if (
        action.failurePolicy !== undefined &&
        action.failurePolicy !== "fail_fast"
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["failurePolicy"],
          message: "Withdrawal batches require fail_fast",
        });
      }
      return;
    }
    if (action.type === "spl_transfer") {
      const asset = Parser.parseAssetId(action.args.assetId);
      const assetChainRef = `${asset.chain.namespace}:${asset.chain.reference}`;
      if (
        asset.chain.namespace !== "solana" ||
        asset.asset.namespace !== "spl" ||
        assetChainRef !== action.scope.chainRef ||
        !SolanaAddressSchema.safeParse(asset.asset.reference).success
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "spl_transfer assetId must be a Solana SPL asset on scope.chainRef",
          path: ["args", "assetId"],
        });
      }
      return;
    }
    if (action.type === "ondo_direct_batch_rebalance") {
      addOndoDirectBatchScopeChainIssues({
        chainId: action.scope.chainId,
        args: action.args,
        ctx,
      });
      return;
    }
    if (action.type === "reality_via_bitget_swap") {
      addRealityViaBitgetScopeChainIssues({
        chainId: action.scope.chainId,
        args: action.args,
        ctx,
      });
      return;
    }
    if (action.type === ACTION_FAMILY.swap && "cashAssetId" in action.args) {
      addOndoDirectBatchScopeChainIssues({
        chainId: action.scope.chainId,
        args: action.args,
        ctx,
      });
    }
  });
